import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { addDays, format } from 'date-fns';
import {
  DxButtonModule,
  DxContextMenuModule,
  DxDateBoxModule,
  DxPopupModule,
  DxSchedulerModule,
  DxSelectBoxModule,
  DxTemplateModule,
  DxTextAreaModule,
  DxTextBoxModule,
} from 'devextreme-angular';
import { ItemClickEvent } from 'devextreme/ui/context_menu';
import notify from 'devextreme/ui/notify';
import {
  AppointmentContextMenuEvent,
  AppointmentDblClickEvent,
  AppointmentFormOpeningEvent,
  AppointmentRenderedEvent,
  AppointmentUpdatingEvent,
  CellContextMenuEvent,
} from 'devextreme/ui/scheduler';
import { firstValueFrom, forkJoin } from 'rxjs';
import { LoaderComponent } from '../../loader/loader.component';
import { ContextService } from '../../services/context.service';
import {
  CreateDriverNoteDto,
  DriverNoteType,
  DriverState,
  FleetService,
  LicenseType,
  NotificationState,
  ProjectService,
  UpdateDriverDto,
  UpdateDriverNoteDto,
} from '../../shared/generated/backend';
import { ROLE_ADMIN, ROLE_DISPO, SessionService } from '../../shared/services/session.service';
import { SharedModule } from '../../shared/shared.module';
import { createBusyState } from '../../shared/util/busy-state';
import {
  Appointment,
  AppointmentType,
  BaseSchedulerComponent,
  Driver,
  NoteType,
  ToolbarItem,
} from '../base-scheduler-component';
import {
  BadgeVariantComponent,
  Shape,
} from '../../shared/components/badge-variant/badge-variant/badge-variant.component';

const DRIVER_NOTIFICATION_COLORS = {
  [DriverState.Confirmed]: '#6BCB77',
  [DriverState.Read]: '#FFD93D',
  [DriverState.Notified]: '#FF6B6B',
  [DriverState.NotDispatched]: '#FFFFFF',
} as const;

const NOTE_TYPE_COLORS = {
  [DriverNoteType.Transport]: '#FF6B6B',
  [DriverNoteType.Sick]: '#FF6B6B',
  [DriverNoteType.Vacation]: '#6BCB77',
  [DriverNoteType.Private]: '#FFC107',
  [DriverNoteType.School]: '#DDA0DD',
  [DriverNoteType.Free]: '#FFC107',
  [DriverNoteType.Cancelled]: '#FF6B6B',
} as const;

interface DataSource {
  id: number | string;
  text: string;
}

interface ContextMenuItem {
  id: string;
  text: string;
  items?: Array<ContextMenuItem>;
}

interface DriverAppointmentVm extends Appointment {
  appointmentColor: string;
  driverStateColor: string;
  noteTypeIcon: string;
  isWeekAppointment: boolean;
  appointmentCssClass: string;
  displayVehicleText: string;
  isPrime: boolean;
}

@Component({
  selector: 'app-driver-schedule',
  imports: [
    DxSchedulerModule,
    CommonModule,
    LoaderComponent,
    DxPopupModule,
    DxTextBoxModule,
    DxButtonModule,
    DxTemplateModule,
    DxDateBoxModule,
    DxSelectBoxModule,
    DxTextAreaModule,
    DxContextMenuModule,
    SharedModule,
    BadgeVariantComponent,
  ],
  templateUrl: './driver-schedule.component.html',
  styleUrls: ['./driver-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DriverScheduleComponent extends BaseSchedulerComponent implements OnInit {
  // Driver-specific properties
  AppointmentType = AppointmentType;
  readonly Shape = Shape;
  selectedDrivers: number[] = [];
  // add 'drivingLicense' to add new grouping
  groups = ['DriverId'];
  contextMenuItems: ContextMenuItem[] = [];
  private contextMenuItemOptions: ContextMenuItem[] = [];

  isPopupVisible = false;
  contextMenuVisible = false;
  noteTypes: { display: string; value: string }[] = [];
  noteTypeValidationStatus: 'valid' | 'invalid' = 'valid';
  isNoteTypeValid = true;
  isEditMode = false;
  /** Guards all mutating actions (notes, week appointments, notification state) against double triggering. */
  readonly busy = createBusyState();

  newAppointmentData: Appointment = {
    startDate: new Date(),
    endDate: new Date(),
    Text: '',
    NoteType: null,
    DriverId: 0,
    AppointmentType: AppointmentType.NOTE,
    Id: 0,
    ProjectNo: 0,
    ProcessingState: 'ProcessingByWarehouse',
    FromDateTime: new Date().toISOString(),
    UntilDateTime: new Date().toISOString(),
    PickupWeight: 0,
    PickupVolume: 0,
    DeliveryWeight: 0,
    DeliveryVolume: 0,
    DriverState: DriverState.NotDispatched,
    NotificationSend: NotificationState.Nothing,
    Priority: 1,
    isCourier: false,
    Loadunits: [],
  };

  private contextMenuData: CellContextMenuEvent | AppointmentContextMenuEvent | null = null;

  private readonly drivingLicenseResource = {
    fieldExpr: 'drivingLicense',
    allowMultiple: false,
    dataSource: [
      { id: LicenseType.Big, text: '40t' },
      { id: LicenseType.Middle, text: '7.5t' },
      { id: LicenseType.Small, text: '3.5t' },
    ] as DataSource[],
    displayExpr: 'text',
    valueExpr: 'id',
  };

  private readonly driverResource = {
    fieldExpr: 'DriverId',
    allowMultiple: false,
    dataSource: [] as Driver[],
    displayExpr: 'text',
    valueExpr: 'id',
  };

  resources: {
    fieldExpr: string;
    allowMultiple: boolean;
    dataSource: Driver[] | DataSource[];
    displayExpr: string;
    valueExpr: string;
  }[] = [];

  private fleetService = inject(FleetService);
  private projectService = inject(ProjectService);
  private contextService = inject(ContextService);
  private sessionService = inject(SessionService);
  private translationService = inject(TranslateService);

  constructor() {
    super();
    this.initializeResources();
  }

  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();
  }

  private initializeResources(): void {
    this.driverResource.dataSource = this.drivers;
    this.resources = [this.drivingLicenseResource, this.driverResource];
  }

  protected override async loadInitialData(): Promise<void> {
    this.contextMenuItemOptions = [
      { text: this.translationService.instant('SCHEDULE.LONG_WEEK'), id: 'add-long-week' },
      { text: this.translationService.instant('SCHEDULE.SHORT_WEEK'), id: 'add-short-week' },
      {
        text: this.translationService.instant('SCHEDULE.NOTIFICATION_UPDATE_STATE'),
        id: 'update-notification',
        items: [
          {
            text: this.translationService.instant('SCHEDULE.NOTIFICATION_NOTIFIED'),
            id: 'update-notification-notified',
          },
          { text: this.translationService.instant('SCHEDULE.NOTIFICATION_READ'), id: 'update-notification-read' },
          {
            text: this.translationService.instant('SCHEDULE.NOTIFICATION_CONFIRMED'),
            id: 'update-notification-confirmed',
          },
        ],
      },
    ];

    this.noteTypes = Object.values(NoteType).map(value => ({
      value: value,
      display: this.translationService.instant('NOTE_TYPE.' + value.toString()),
    }));

    this.cdr.markForCheck();
    await this.loadDrivers();
    await this.loadAppointments();
  }

  protected async loadDrivers(updateResources = true): Promise<void> {
    try {
      const drivers = await firstValueFrom(this.fleetService.getAllDrivers());
      this.drivers = drivers.map(driver => ({
        ...driver,
        id: driver.employeeNumber,
        text: `${driver.firstName} ${driver.lastName}`,
      }));

      if (this.selectedDrivers.length === 0) {
        this.selectedDrivers = this.drivers.map(d => d.employeeNumber);
      } else {
        const existing = new Set(this.drivers.map(d => d.employeeNumber));
        const preserved = this.selectedDrivers.filter(id => existing.has(id));
        this.selectedDrivers = preserved.length ? preserved : this.drivers.map(d => d.employeeNumber);
      }

      if (updateResources) {
        this.updateResourcesDataSource();
      }

      this.syncToolbarState();
      this.cdr.markForCheck();
    } catch (err: unknown) {
      if (typeof err === 'object' && err !== null && 'error' in err) {
        const errorMessage = typeof err.error === 'string' ? err.error : 'GENERAL';
        notify(this.translationService.instant('ERROR.' + errorMessage), 'error', 3000);
      } else {
        notify(this.translationService.instant('ERROR.GENERAL'), 'error', 3000);
      }
      console.error(err);
    }
  }

  override async loadAppointments(): Promise<void> {
    this.isLoading = true;
    this.cdr.markForCheck();

    const scrollPosition = this.getScrollPosition('.driver-name');

    this.appointments = [];
    await this.loadDrivers();

    const { fromDate, toDate } = this.getUpdatedDateRange();
    // Add one day to the toDate to include the whole day
    try {
      const { notes, projects } = await firstValueFrom(
        forkJoin({
          notes: this.fleetService.getDriversNotes(
            format(fromDate, 'yyyy-MM-dd'),
            format(addDays(toDate, 1), 'yyyy-MM-dd')
          ),
          projects: this.projectService.getProjects(
            format(fromDate, 'yyyy-MM-dd'),
            format(addDays(toDate, 1), 'yyyy-MM-dd')
          ),
        })
      );

      const noteAppointments = notes.map(note => {
        const driver = this.drivers.find(d => d.employeeNumber === note.DriverId);
        return this.toAppointmentVm({
          ...note,
          Text: note.Text,
          startDate: new Date(note.StartDate),
          endDate: new Date(note.EndDate),
          drivingLicense: driver?.drivingLicence,
          AppointmentType: AppointmentType.NOTE,
          NotificationState: null,
          NoteType: note.Type,
          Id: note.Id,
          ProjectNo: -1,
          ProcessingState: 'ProcessingByWarehouse',
          FromDateTime: note.StartDate,
          UntilDateTime: note.EndDate,
          PickupWeight: 0,
          PickupVolume: 0,
          DeliveryWeight: 0,
          DeliveryVolume: 0,
        } as unknown as Appointment);
      });
      // console.log('Note appointments', noteAppointments);

      const projectAppointments = projects
        // .filter(p => p.DriverState != DriverState.NotDispatched)
        .map(project => {
          const driver = this.drivers.find(d => d.employeeNumber === project.DriverId);
          return this.toAppointmentVm({
            ...project,
            Text: project.TrapoText || '',
            startDate: project.FromDateTime ? new Date(project.FromDateTime) : new Date(),
            endDate: project.UntilDateTime ? new Date(project.UntilDateTime) : new Date(),
            Driver: driver,
            drivingLicense: driver?.drivingLicence,
            AppointmentType: AppointmentType.PROJECT,
            NoteType: null,
            Id: project.Id,
            ProjectNo: project.ProjectNo,
            ProcessingState: 'ProcessingByWarehouse',
            FromDateTime: project.FromDateTime ?? new Date().toISOString(),
            UntilDateTime: project.UntilDateTime ?? new Date().toISOString(),
            PickupWeight: 0,
            PickupVolume: 0,
            DeliveryWeight: 0,
            DeliveryVolume: 0,
          } as unknown as Appointment);
        });

      this.appointments = [...projectAppointments, ...noteAppointments];
      this.loadWeeksAppointments();
      this.repaintScheduler();

      setTimeout(() => {
        this.isLoading = false;

        const id = scrollPosition.verticalElement ? parseInt(scrollPosition.verticalElement.id, 10) : undefined;
        const date = this.scrollPositionToDate(scrollPosition);
        this.scheduler?.instance.scrollTo(date, { DriverId: id });
        this.cdr.markForCheck();
      }, 100);
    } catch (err: unknown) {
      if (typeof err === 'object' && err !== null && 'error' in err) {
        const errorMessage = typeof err.error === 'string' ? err.error : 'GENERAL';
        notify(this.translationService.instant('ERROR.' + errorMessage), 'error', 3000);
      } else {
        notify(this.translationService.instant('ERROR.GENERAL'), 'error', 3000);
      }
      console.error(err);
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  updateResourcesDataSource(): void {
    const filteredDrivers = this.drivers.filter(d => this.selectedDrivers.includes(d.employeeNumber));

    if (this.sameDriverIds(this.driverResource.dataSource, filteredDrivers)) {
      return;
    }

    this.driverResource.dataSource = filteredDrivers;

    if (this.scheduler?.instance) {
      this.scheduler.instance.option('resources', this.resources);
    }

    this.repaintScheduler();
    this.cdr.markForCheck();
  }

  loadWeeksAppointments(): void {
    this.drivers
      .filter(d => d.weekStart || d.weekStartShort)
      .forEach(d => {
        const weekStart = d.weekStart;
        const weekStartShort = d.weekStartShort;

        if (weekStart) {
          const appointment = this.createWeekAppointment(d, new Date(weekStart), AppointmentType.LONG_WEEK);
          this.appointments.push(this.toAppointmentVm(appointment));
        }

        if (weekStartShort) {
          const appointment = this.createWeekAppointment(d, new Date(weekStartShort), AppointmentType.SHORT_WEEK);
          this.appointments.push(this.toAppointmentVm(appointment));
        }
      });
  }

  private createWeekAppointment(driver: Driver, startDate: Date, appointmentType: AppointmentType): Appointment {
    const endDate = new Date(startDate);
    endDate.setHours(endDate.getHours() + 6);
    return {
      ...driver,
      Text: this.translationService.instant(
        'SCHEDULE.' + (appointmentType === AppointmentType.LONG_WEEK ? 'LONG_WEEK' : 'SHORT_WEEK')
      ),
      startDate,
      endDate,
      NoteType: null,
      drivingLicense: driver?.drivingLicence,
      AppointmentType: appointmentType,
      DriverId: driver.employeeNumber,
      Id: 0,
      ProjectNo: 0,
      ProcessingState: 'ProcessingByWarehouse',
      FromDateTime: startDate.toISOString(),
      UntilDateTime: endDate.toISOString(),
      PickupWeight: 0,
      PickupVolume: 0,
      DeliveryWeight: 0,
      DeliveryVolume: 0,
      DriverState: DriverState.NotDispatched,
      NotificationSend: NotificationState.Nothing,
      Priority: 1,
      isCourier: false,
      Loadunits: [],
    };
  }

  getAppointmentColor(appointment: Appointment): string {
    switch (appointment.AppointmentType) {
      case AppointmentType.PROJECT:
        return this.getProjectColor(appointment);
      case AppointmentType.NOTE:
        return this.getNoteColor(appointment);
      case AppointmentType.LONG_WEEK:
      case AppointmentType.SHORT_WEEK:
        return this.getWeekColor(appointment);
      default:
        return '#FFFFFF';
    }
  }

  getDriverStateColor(appointment: Appointment): string {
    switch (appointment.DriverState) {
      case DriverState.Notified:
        return '#FF6B6B';
      case DriverState.Read:
        return '#FFD93D';
      case DriverState.Confirmed:
        return '#6BCB77';
      default:
        return '#FFFFFF';
    }
  }

  onProjectClick(projectId: number): void {
    // console.log('Project ID clicked:', projectId);
    const windowFeatures = 'width=1200,height=800,left=100,top=100';
    window.open(`/tour(right:project/${projectId})`, '_blank', windowFeatures);
  }

  private getProjectColor(appointment: Appointment): string {
    return appointment.DriverState ? DRIVER_NOTIFICATION_COLORS[appointment.DriverState] : '#FFFFFF';
  }

  private getNoteColor(appointment: Appointment): string {
    return appointment.NoteType ? NOTE_TYPE_COLORS[appointment.NoteType] : '#FFFFFF';
  }

  onAppointmentUpdating(e: AppointmentUpdatingEvent): void {
    const oldDriverId = e.oldData.DriverId;

    // console.log('Updating appointment:', e);

    if (
      e.oldData.AppointmentType === AppointmentType.LONG_WEEK ||
      e.oldData.AppointmentType === AppointmentType.SHORT_WEEK
    ) {
      e.cancel = true;
      return;
    }

    if (this.busy.busy()) {
      e.cancel = true;
      return;
    }

    if (e.oldData.AppointmentType === AppointmentType.NOTE) {
      const updateNoteData: UpdateDriverNoteDto = {
        Text: e.newData.Text ?? e.oldData.Text ?? '',
        Type: e.newData.NoteType ?? e.oldData.NoteType,
        StartDate: this.formatDateForBackend(e.newData.startDate),
        EndDate: this.formatDateForBackend(e.newData.endDate),
        Id: e.oldData.Id,
      };
      // console.log('Updating note:', updateNoteData);
      void this.busy.run(() => this.updateNote(updateNoteData, oldDriverId));
    }
  }

  onAppointmentFormOpening(e: AppointmentFormOpeningEvent): void {
    e.cancel = true;
    const appointmentData = e.appointmentData as Appointment;
    // console.log('Opening form for appointment:', appointmentData);
    if (
      appointmentData.AppointmentType === AppointmentType.LONG_WEEK ||
      appointmentData.AppointmentType === AppointmentType.SHORT_WEEK ||
      appointmentData.AppointmentType === AppointmentType.PROJECT
    ) {
      e.cancel = true;
      return;
    }
    this.newAppointmentData = { ...appointmentData };
    this.newAppointmentData.Text = appointmentData.Text;
    this.newAppointmentData.NoteType = appointmentData.NoteType
      ? (appointmentData.NoteType.toUpperCase() as DriverNoteType)
      : null;

    this.isPopupVisible = true;
    this.cdr.markForCheck();
  }

  onDriverSelectionChanged(e: { value: number[] }): void {
    this.selectedDrivers = e.value;
    this.updateResourcesDataSource();
    this.syncToolbarState();
    this.cdr.markForCheck();
  }

  onPopupHiding(): void {
    this.isPopupVisible = false;
    this.noteTypeValidationStatus = 'valid';
    this.resetForm();
    this.cdr.markForCheck();
  }

  onAppointmentDblClick(e: AppointmentDblClickEvent): void {
    const appointment = e.appointmentData as Appointment;
    if (
      appointment.AppointmentType === AppointmentType.LONG_WEEK ||
      appointment.AppointmentType === AppointmentType.SHORT_WEEK ||
      appointment.AppointmentType === AppointmentType.PROJECT
    ) {
      this.openProjectContextView(e);
      e.cancel = true;
      return;
    }
    this.isEditMode = true;
    this.newAppointmentData = appointment;
    // console.log('New appointment data:', this.newAppointmentData);
    this.isPopupVisible = true;
    this.cdr.markForCheck();
  }

  openProjectContextView(e: AppointmentDblClickEvent): void {
    e.cancel = true; // Prevent the default edit window from opening
    const appointmentData = e.appointmentData as Appointment;
    if (appointmentData && appointmentData.ProjectNo) {
      this.contextService.openContextView(`project/${appointmentData.ProjectNo}`);
    } else {
      console.error('ProjectNo not found in appointment data:', appointmentData);
    }
  }

  getNoteTypeIcon(noteType: DriverNoteType | null | undefined): string {
    if (!noteType) return 'dx-icon-comment';

    switch (noteType) {
      case DriverNoteType.Sick:
        return 'dx-icon-like';
      case DriverNoteType.Vacation:
        return 'dx-icon-airplane';
      case DriverNoteType.Private:
        return 'dx-icon-user';
      case DriverNoteType.School:
        return 'dx-icon-group';
      case DriverNoteType.Free:
        return 'dx-icon-sun';
      case DriverNoteType.Cancelled:
        return 'dx-icon-remove';
      default:
        return 'dx-icon-comment';
    }
  }

  onFormSubmit(e: Event): void {
    // console.log('Form submitted:', this.newAppointmentData);
    e.preventDefault();
    if (this.busy.busy()) return;
    if (!this.newAppointmentData.NoteType) {
      this.noteTypeValidationStatus = 'invalid';
      this.cdr.markForCheck();
      return;
    }

    if (this.isEditMode) {
      const updateNoteData: UpdateDriverNoteDto = {
        StartDate: this.formatDateForBackend(this.newAppointmentData.startDate!),
        EndDate: this.formatDateForBackend(this.newAppointmentData.endDate!),
        Text: this.newAppointmentData.Text,
        Type: this.newAppointmentData.NoteType,
        Id: this.newAppointmentData.Id,
      };
      void this.busy.run(() => this.updateNote(updateNoteData, this.newAppointmentData.DriverId!));
    } else {
      const noteData: CreateDriverNoteDto = {
        DriverId: this.newAppointmentData.DriverId!,
        StartDate: this.formatDateForBackend(this.newAppointmentData.startDate!),
        EndDate: this.formatDateForBackend(this.newAppointmentData.endDate!),
        Text: this.newAppointmentData.Text || ' ',
        Type: this.newAppointmentData.NoteType,
      };
      void this.busy.run(() => this.addNote(noteData, this.newAppointmentData.DriverId!));
    }
  }

  private async updateNote(updateNoteData: UpdateDriverNoteDto, driverId: number): Promise<void> {
    try {
      await firstValueFrom(this.fleetService.updateDriverNote(driverId, updateNoteData));
    } catch (error) {
      console.error('Error adding note:', error);
      notify(this.translationService.instant('SCHEDULE.NOTE_UPDATE_FAIL'), 'error', 2000);
      return;
    }
    notify(this.translationService.instant('SCHEDULE.NOTE_UPDATE_SUCCESS'), 'success', 2000);
    this.closeAndResetForm();
    await this.loadAppointments();
  }

  private async addNote(noteData: CreateDriverNoteDto, driverId: number): Promise<void> {
    try {
      await firstValueFrom(this.fleetService.addDriverNote(driverId, noteData));
    } catch (error) {
      console.error('Error adding note:', error);
      notify(this.translationService.instant('SCHEDULE.NOTE_ADD_FAIL'), 'error', 2000);
      return;
    }
    notify(this.translationService.instant('SCHEDULE.NOTE_ADD_SUCCESS'), 'success', 2000);
    this.closeAndResetForm();
    await this.loadAppointments();
  }

  private closeAndResetForm(): void {
    this.isPopupVisible = false;
    this.resetForm();
    this.repaintScheduler();
    this.cdr.markForCheck();
  }

  onCellContextMenu(e: CellContextMenuEvent | AppointmentContextMenuEvent): void {
    const isAppointment = 'appointmentData' in e && e.appointmentData['AppointmentType'] === 'PROJECT';

    this.contextMenuData = e;
    if (
      isAppointment &&
      (this.sessionService.getRoles.includes(ROLE_DISPO) || this.sessionService.getRoles.includes(ROLE_ADMIN))
    ) {
      this.contextMenuItems = this.contextMenuItemOptions.filter(item => {
        return item.id === 'update-notification' || item.id === 'add-long-week' || item.id === 'add-short-week';
      });
    } else if (isAppointment) {
      this.contextMenuItems = this.contextMenuItemOptions.filter(item => {
        return item.id === 'add-long-week' || item.id === 'add-short-week';
      });
    } else {
      this.contextMenuItems = this.contextMenuItemOptions.filter(item => {
        return item.id === 'add-long-week' || item.id === 'add-short-week';
      });
    }

    this.contextMenuVisible = true;
    this.cdr.markForCheck();
  }

  onContextMenuItemClick(e: ItemClickEvent): void {
    const itemData = e.itemData as ContextMenuItem;
    switch (itemData.id) {
      case 'add-long-week':
        this.addWeekAppointment(AppointmentType.LONG_WEEK);
        break;
      case 'add-short-week':
        this.addWeekAppointment(AppointmentType.SHORT_WEEK);
        break;
      case 'update-notification-notified':
        this.updateNotificationState(DriverState.Notified);
        break;
      case 'update-notification-read':
        this.updateNotificationState(DriverState.Read);
        break;
      case 'update-notification-confirmed':
        this.updateNotificationState(DriverState.Confirmed);
        break;
    }
    this.contextMenuVisible = false;
    this.cdr.markForCheck();
  }

  async updateNotificationState(state: DriverState) {
    const data =
      (this.contextMenuData as CellContextMenuEvent).cellData ??
      (this.contextMenuData as AppointmentContextMenuEvent).appointmentData;
    const projectNumber: number = data.ProjectNo;
    if (!projectNumber) return;
    await this.busy.run(async () => {
      try {
        await firstValueFrom(this.projectService.updateNotificationStatus(projectNumber, { state: state }));
      } catch (error) {
        // 404: the project's current driver has no dispo record yet (never notified, or the
        // driver was changed). The override only corrects an existing state.
        console.error('Error updating notification state:', error);
        notify(this.translationService.instant('SCHEDULE.NOTIFICATION_STATE_FAIL'), 'error', 4000);
      }
      await this.loadAppointments();
    });
  }

  onDeleteNote() {
    // console.log('Deleting note:', this.newAppointmentData);
    if (!this.newAppointmentData || this.busy.busy()) return;
    const targetedAppointmentData = this.newAppointmentData;
    if (confirm(this.translationService.instant('SCHEDULE.ASK_DELETE'))) {
      void this.busy.run(async () => {
        try {
          await firstValueFrom(this.fleetService.deleteDriverNote(targetedAppointmentData.Id));
        } catch (error) {
          console.error('Error deleting note:', error);
          notify(this.translationService.instant('SCHEDULE.DELETE_FAIL'), 'error', 2000);
          return;
        }
        notify(this.translationService.instant('SCHEDULE.DELETE_SUCCESS'), 'success', 2000);
        this.closeAndResetForm();
        await this.loadAppointments();
      });
      const index = this.appointments.findIndex(
        a => a.startDate === targetedAppointmentData.startDate && a.DriverId === targetedAppointmentData.DriverId
      );

      if (index > -1) {
        this.appointments.splice(index, 1);
      }
      this.isPopupVisible = false;
      this.repaintScheduler();
      this.cdr.markForCheck();
    }
  }

  onRemoveWeekAppointment(appointment: Appointment): void {
    if (this.busy.busy()) return;
    const driverId = appointment.DriverId;
    const driver = this.drivers.find(d => d.employeeNumber === driverId);
    const updateData: UpdateDriverDto = {
      weekStart: appointment.AppointmentType === AppointmentType.LONG_WEEK ? '' : driver?.weekStart,
      weekStartShort: appointment.AppointmentType === AppointmentType.SHORT_WEEK ? '' : driver?.weekStartShort,
      priority: driver!.priority,
      employeeNumber: driverId,
    };

    void this.updateDriver(driverId as number, updateData);
  }

  onTemplateClick(event: MouseEvent, appointmentData: Appointment): void {
    event.stopPropagation();
    const projectId = appointmentData.ProjectNo;
    this.onProjectClick(projectId!);
  }

  private addWeekAppointment(appointmentType: AppointmentType): void {
    if (this.busy.busy()) return;
    const data =
      (this.contextMenuData as CellContextMenuEvent).cellData ??
      (this.contextMenuData as AppointmentContextMenuEvent).appointmentData;
    const startDate = new Date(data.startDate);
    startDate.setHours(startDate.getHours());
    const endDate = new Date(data.startDate);
    endDate.setHours(endDate.getHours() + 4);
    const driverId = data.DriverId ?? data.groups.DriverId;

    if (this.hasOverlappingWeek(startDate, endDate, driverId)) {
      notify(this.translationService.instant('SCHEDULE.ADD_OVERLAPPING_WEEKS'), 'warning', 2000);
      return;
    }

    // Find the driver to update
    const driver = this.drivers.find(d => d.employeeNumber === driverId);
    if (!driver) {
      notify(this.translationService.instant('SCHEDULE.DRIVER_NOT_FOUND'), 'error', 2000);
      return;
    }

    const updateData: UpdateDriverDto = {
      weekStart:
        appointmentType === AppointmentType.LONG_WEEK ? format(startDate!, 'yyyy-MM-dd HH:mm:ss') : driver?.weekStart,
      weekStartShort:
        appointmentType === AppointmentType.SHORT_WEEK
          ? format(startDate!, 'yyyy-MM-dd HH:mm:ss')
          : driver!.weekStartShort,
      priority: driver!.priority,
    };
    // console.log('Update data:', updateData);
    void this.updateDriver(driverId, updateData);
  }

  private updateDriver(driverId: number, updateData: UpdateDriverDto): Promise<void | undefined> {
    return this.busy.run(async () => {
      try {
        await firstValueFrom(this.fleetService.updateDriver(driverId, updateData));
      } catch (error) {
        console.error('Error updating driver week:', error);
        notify(this.translationService.instant('SCHEDULE.UPDATE_FAIL'), 'error', 2000);
        return;
      }
      notify(this.translationService.instant('SCHEDULE.UPDATE_SUCCESS'), 'success', 2000);
      // loadAppointments() also reloads the drivers
      await this.loadAppointments();
    });
  }

  private hasOverlappingWeek(startDate: Date, endDate: Date, driverId: number): boolean {
    return this.appointments.some(appointment => {
      if (appointment.DriverId !== driverId) return false;
      if (
        appointment.AppointmentType !== AppointmentType.LONG_WEEK &&
        appointment.AppointmentType !== AppointmentType.SHORT_WEEK
      )
        return false;

      return startDate < appointment.endDate! && endDate > appointment.startDate!;
    });
  }

  private resetForm(): void {
    this.newAppointmentData = {
      startDate: new Date(),
      endDate: new Date(),
      Text: '',
      NoteType: null,
      DriverId: 0,
      AppointmentType: AppointmentType.NOTE,
      Id: 0,
      ProjectNo: 0,
      ProcessingState: 'ProcessingByWarehouse',
      FromDateTime: new Date().toISOString(),
      UntilDateTime: new Date().toISOString(),
      PickupWeight: 0,
      PickupVolume: 0,
      DeliveryWeight: 0,
      DeliveryVolume: 0,
      DriverState: DriverState.NotDispatched,
      NotificationSend: NotificationState.Nothing,
      Priority: 1,
      RampNeeded: true,
      isCourier: false,
      Loadunits: [],
    };
    this.isEditMode = false;
  }

  // Override parent method to add driver-specific toolbar items
  protected override prepareToolbar(): void {
    super.prepareToolbar();
    const toolbarOrder = ['dateRange', 'driverFilter', 'viewSwitcher', 'refreshButton', 'autoPolling'];
    const toolbarInstance = this.getToolbarInstance();
    if (!toolbarInstance) {
      return;
    }

    const items = [...(toolbarInstance.option('items') as ToolbarItem[])];

    this.upsertToolbarItem(items, {
      widget: 'dxTagBox',
      name: 'driverFilter',
      options: {
        dataSource: this.drivers,
        displayExpr: 'text',
        valueExpr: 'employeeNumber',
        value: this.selectedDrivers,
        onValueChanged: (e: { value: number[] }) => this.onDriverSelectionChanged(e),
        placeholder: this.translationService.instant('SCHEDULE.SELECT_DRIVERS'),
        showClearButton: true,
        searchEnabled: true,
        width: 200,
        multiline: false,
        maxDisplayedTags: 2,
        showSelectionControls: true,
        applyValueMode: 'useButtons',
        stylingMode: 'outlined',
        onOpened: () => this.toolboxWidgetOpened(),
        onClosed: () => this.toolboxWidgetClosed(),
      },
      location: 'before',
      locationInMenu: 'always',
    });

    items.sort((a, b) => {
      const ai = a.name ? toolbarOrder.indexOf(a.name) : Number.MAX_SAFE_INTEGER;
      const bi = b.name ? toolbarOrder.indexOf(b.name) : Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });

    toolbarInstance.option('items', items);
  }

  onAppointmentRendered(e: AppointmentRenderedEvent) {
    const appointmentData = e.appointmentData as Appointment;
    if (
      appointmentData.AppointmentType === AppointmentType.LONG_WEEK ||
      appointmentData.AppointmentType === AppointmentType.SHORT_WEEK
    ) {
      e.appointmentElement.classList.add('week-appointment-height');
    }

    if (appointmentData.AppointmentType === AppointmentType.PROJECT) {
      const element = e.appointmentElement;

      // Remove resize handles
      element.classList.add('no-resize');
    }
  }

  // Add this helper method near the top of the class
  private formatDateForBackend(date: Date | undefined): string {
    const safeDate = date ?? new Date();
    // Format as YYYY-MM-DD HH:mm:ss
    return `${safeDate.getFullYear()}-${String(safeDate.getMonth() + 1).padStart(2, '0')}-${String(safeDate.getDate()).padStart(2, '0')} ${String(safeDate.getHours()).padStart(2, '0')}:${String(safeDate.getMinutes()).padStart(2, '0')}:${String(safeDate.getSeconds()).padStart(2, '0')}`;
  }

  private toAppointmentVm(appointment: Appointment): DriverAppointmentVm {
    const isWeekAppointment =
      appointment.AppointmentType === AppointmentType.LONG_WEEK ||
      appointment.AppointmentType === AppointmentType.SHORT_WEEK;

    return {
      ...appointment,
      appointmentColor: this.getAppointmentColor(appointment),
      driverStateColor: this.getDriverStateColor(appointment),
      noteTypeIcon: this.getNoteTypeIcon(appointment.NoteType),
      isWeekAppointment,
      appointmentCssClass: this.getAppointmentClass(appointment),
      displayVehicleText: (appointment as { Vehicle?: string; text?: string }).Vehicle ?? '',
      isPrime: (appointment as unknown as { isPrime?: boolean }).isPrime ?? false,
    };
  }

  private sameDriverIds(current: Driver[], next: Driver[]): boolean {
    if (current.length !== next.length) {
      return false;
    }

    for (let i = 0; i < current.length; i += 1) {
      if (current[i].employeeNumber !== next[i].employeeNumber) {
        return false;
      }
    }

    return true;
  }

  private syncToolbarState(): void {
    this.syncToolbarItemOptions('driverFilter', {
      dataSource: this.drivers,
      value: this.selectedDrivers,
    });
  }
}
